import { ReflectiveInjector, Injector } from '@angular/core';

import { KeyValuePair, Team } from './index';
import { PaymentOptionTypeService, RegistrationTypeService } from '../service/index';
import { convertToDateTime } from '../helper/dateTime-helper';

/**
 * Model for a division's registration data. This will be used to control
 * the registration process for an event and specific wrestler division. For example,
 * it defines when online (pre-registration) will be available and how much it will cost.
 */
export class DivisionRegistration {
    public id: string = null;
    public eventId: string = null;
    public eventTimeZone: string = null;
    public divisionId: string = null;
    public registrationType: KeyValuePair = null;
    public paymentOptionType: KeyValuePair = null;
    public requireApproxWeight: boolean = false;
    public onlineStartDateTime: Date;
    public onlineEndDateTime: Date;
    public onlineIndividualPrice: number = 0;
    public onlineTeamPrice: number = 0;
    public onsiteStartDateTime: Date;
    public onsiteEndDateTime: Date;
    public onsiteIndividualPrice: number = 0;
    public onsiteTeamPrice: number = 0;
    public teams: Team[] = null;

    private registrationTypeService: RegistrationTypeService = null;
    private paymentOptionTypeService: PaymentOptionTypeService = null;

    /**
     * Constructor
     */
    constructor(eventId?: string, divisionId?: string) {
        this.eventId = eventId;
        this.divisionId = divisionId;
        this.registrationType = new KeyValuePair(RegistrationTypeService.NONE, 'None');
    }

    public hasOnlinePayment(): boolean {
        return this.paymentOptionType &&
            (
                this.paymentOptionType.key === PaymentOptionTypeService.ONLINE_ONLY ||
                this.paymentOptionType.key === PaymentOptionTypeService.ONSITE_ONLINE
            );
    }

    public hasOnsitePayment(): boolean {
        return this.paymentOptionType &&
            (
                this.paymentOptionType.key === PaymentOptionTypeService.ONSITE_ONLY ||
                this.paymentOptionType.key === PaymentOptionTypeService.ONSITE_ONLINE
            );
    }

    /**
     * Clone the DivisionRegistration object. This is used to prevent object reference issues
     */
    public clone(): DivisionRegistration {
        const clone: DivisionRegistration = new DivisionRegistration(this.eventId, this.divisionId);
        clone.id = this.id;

        if (this.registrationType) {
            clone.registrationType = this.registrationType.clone();
        }
        if (this.paymentOptionType) {
            clone.paymentOptionType = this.paymentOptionType.clone();
        }
        clone.requireApproxWeight = this.requireApproxWeight;

        if (this.onlineStartDateTime) {
            clone.onlineStartDateTime = new Date(this.onlineStartDateTime.getTime());
        }
        if (this.onlineEndDateTime) {
            clone.onlineEndDateTime = new Date(this.onlineEndDateTime.getTime());
        }
        clone.onlineIndividualPrice = this.onlineIndividualPrice;
        clone.onlineTeamPrice = this.onlineTeamPrice;

        if (this.onsiteStartDateTime) {
            clone.onsiteStartDateTime = new Date(this.onsiteStartDateTime.getTime());
        }
        if (this.onsiteEndDateTime) {
            clone.onsiteEndDateTime = new Date(this.onsiteEndDateTime.getTime());
        }
        clone.onsiteIndividualPrice = this.onsiteIndividualPrice;
        clone.onsiteTeamPrice = this.onsiteTeamPrice;
        clone.eventTimeZone = this.eventTimeZone;

        if (this.teams) {
            clone.teams = this.teams.map((team: Team) => team.clone());
        }

        if (this.teams) {
            clone.teams = this.teams.map((team: Team) => team.clone());
        }

        return clone;
    }

    /**
     * Serialize from a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'divisionRegistration';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.relationships = {};
        json.data.attributes.registrationType = this.registrationType.key;
        json.data.attributes.paymentOptionType = null;
        json.data.attributes.onlineStartDateTimeUtc = null;
        json.data.attributes.onlineEndDateTimeUtc = null;
        json.data.attributes.onsiteStartDateTimeUtc = null;
        json.data.attributes.onsiteEndDateTimeUtc = null;

        if (this.paymentOptionType) {
            json.data.attributes.paymentOptionType = this.paymentOptionType.key;
        }

        json.data.attributes.requireApproxWeight = this.requireApproxWeight;

        if (this.onlineStartDateTime && this.onlineEndDateTime) {
            json.data.attributes.onlineStartDateTimeUtc = this.onlineStartDateTime.toISOString();
            json.data.attributes.onlineEndDateTimeUtc = this.onlineEndDateTime.toISOString();
        }
        if (this.onsiteStartDateTime && this.onsiteEndDateTime) {
            json.data.attributes.onsiteStartDateTimeUtc = this.onsiteStartDateTime.toISOString();
            json.data.attributes.onsiteEndDateTimeUtc = this.onsiteEndDateTime.toISOString();
        }

        json.data.attributes.onlineIndividualPrice = parseFloat(this.onlineIndividualPrice.toString());
        json.data.attributes.onsiteIndividualPrice = parseFloat(this.onsiteIndividualPrice.toString());

        json.data.attributes.onlineTeamPrice = parseFloat(this.onlineTeamPrice.toString());
        json.data.attributes.onsiteTeamPrice = parseFloat(this.onsiteTeamPrice.toString());

        const divisionRelationship: any = {};
        divisionRelationship.data = { type: 'division', id: this.divisionId };
        json.data.relationships.division = divisionRelationship;

        if (this.teams) {
            const teams: any = {};
            teams.data = [];

            for (const team of this.teams) {
                teams.data.push({ type: 'team', id: team.id });
            }

            json.data.relationships.teams = teams;
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize to a json api format
     */
    public deserialize(json: Object, included: any): DivisionRegistration {
        const data: Object = json['data'] || json;
        const attrs: Object = data['attributes'];
        const relationships: Object[] = data['relationships'] || [];

        // We need to call other services (they don't actually hit an api) so we will
        // use dependency injection to construct them
        if (this.registrationTypeService === null ||
            this.paymentOptionTypeService === null) {
            const options = {
                providers: [
                    { provide: RegistrationTypeService, useClass: RegistrationTypeService },
                    { provide: PaymentOptionTypeService, useClass: PaymentOptionTypeService },
                ],
            };
            const injector: Injector = Injector.create(options);
            this.registrationTypeService = injector.get(RegistrationTypeService);
            this.paymentOptionTypeService = injector.get(PaymentOptionTypeService);
        }

        this.id = data['id'];
        this.registrationType = this.registrationTypeService.get(attrs['registrationType']);

        if (attrs['paymentOptionType']) {
            this.paymentOptionType = this.paymentOptionTypeService.get(attrs['paymentOptionType']);
        }
        this.requireApproxWeight = attrs['requireApproxWeight'];

        if (attrs['onlineStartDateTime']) {
            this.onlineStartDateTime = convertToDateTime(attrs['onlineStartDateTime']);
        }
        if (attrs['onlineEndDateTime']) {
            this.onlineEndDateTime = convertToDateTime(attrs['onlineEndDateTime']);
        }
        this.onlineIndividualPrice = attrs['onlineIndividualPrice'] ? parseFloat(attrs['onlineIndividualPrice']) : 0;
        this.onlineTeamPrice = attrs['onlineTeamPrice'] ? parseFloat(attrs['onlineTeamPrice']) : 0;

        if (attrs['onsiteStartDateTime']) {
            this.onsiteStartDateTime = convertToDateTime(attrs['onsiteStartDateTime']);
        }
        if (attrs['onsiteEndDateTime']) {
            this.onsiteEndDateTime = convertToDateTime(attrs['onsiteEndDateTime']);
        }

        this.eventTimeZone = attrs['eventTimeZone'];
        this.onsiteIndividualPrice = attrs['onsiteIndividualPrice'] ? parseFloat(attrs['onsiteIndividualPrice']) : 0;
        this.onsiteTeamPrice = attrs['onsiteTeamPrice'] ? parseFloat(attrs['onsiteTeamPrice']) : 0;

        const divisionLink: Object = relationships['division'];
        if (divisionLink &&
            included &&
            divisionLink['data']) {
            this.divisionId = divisionLink['data']['id'];
        }

        const teamLinks: Object = relationships['teams'];

        if (teamLinks &&
            included &&
            teamLinks['data']) {
            this.teams = [];

            for (const teamLink of teamLinks['data']) {
                const team: Object = included.filter((i: Object) => i['id'] === teamLink['id'])[0];

                if (team) {
                    this.teams.push((new Team(this.eventId)).deserialize(team, included));
                }
            }
        }

        return this;
    }
}
